import React, { useEffect, useState } from "react";
import { api } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { X, ShieldCheck } from "lucide-react";

// Modal to edit a user's fine-grained permissions + apply a role template.
// Called from AdminUsers.jsx with the target user + onSaved refetch.
export default function PermissionsDialog({ user, onClose, onSaved }) {
  const [catalog, setCatalog] = useState(null);
  const [selected, setSelected] = useState(new Set(user?.permissions || []));
  const [busy, setBusy] = useState(false);
  const [templateKey, setTemplateKey] = useState("");

  useEffect(() => {
    api.get("/admin/permissions/catalog").then(({ data }) => setCatalog(data))
       .catch(() => toast.error("Failed to load permissions catalog"));
  }, []);

  const toggle = (key) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const applyTemplate = async () => {
    if (!templateKey) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/users/${user.id}/permissions/apply-template`, { template_key: templateKey });
      setSelected(new Set(data.permissions));
      toast.success("Template applied");
      onSaved?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to apply template"); }
    finally { setBusy(false); }
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/admin/users/${user.id}/permissions`, {
        permissions: Array.from(selected),
      });
      toast.success("Permissions updated");
      onSaved?.();
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to save"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" data-testid="permissions-dialog" onClick={onClose}>
      <div className="bg-white rounded-sm shadow-2xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-[#E9ECEF] flex items-center gap-3">
          <ShieldCheck size={18} className="text-[#D4AF37]" />
          <div className="flex-1">
            <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#D4AF37]">Fine-Grained Access</div>
            <h3 className="font-display text-xl font-black text-[#0B132B] leading-tight">{user.name || user.email}</h3>
            <div className="text-xs text-[#6C757D]">{user.email} · {user.role}</div>
          </div>
          <button onClick={onClose} className="text-[#6C757D] hover:text-[#0B132B]" data-testid="permissions-close">
            <X size={18} />
          </button>
        </div>

        <div className="px-6 py-4 overflow-y-auto flex-1">
          {!catalog ? (
            <div className="text-sm text-[#6C757D]">Loading catalog…</div>
          ) : (
            <>
              <div className="mb-5">
                <div className="text-[10px] uppercase tracking-[0.14em] font-bold text-[#6C757D] mb-2">Apply a role template</div>
                <div className="flex items-center gap-2">
                  <select
                    data-testid="template-select"
                    value={templateKey}
                    onChange={(e) => setTemplateKey(e.target.value)}
                    className="flex-1 text-sm rounded-sm border border-[#E9ECEF] bg-white px-3 py-2"
                  >
                    <option value="">Choose a preset…</option>
                    {catalog.role_templates.map((t) => (
                      <option key={t.key} value={t.key}>{t.label}</option>
                    ))}
                  </select>
                  <Button size="sm" disabled={!templateKey || busy} onClick={applyTemplate}
                          className="rounded-sm bg-[#0B132B] hover:bg-[#1C2541] text-white text-[11px] uppercase tracking-[0.14em] font-bold"
                          data-testid="template-apply">
                    Apply
                  </Button>
                </div>
                {templateKey && (
                  <div className="text-[11px] text-[#6C757D] mt-1.5 italic">
                    {catalog.role_templates.find((t) => t.key === templateKey)?.description}
                  </div>
                )}
              </div>

              <div className="text-[10px] uppercase tracking-[0.14em] font-bold text-[#6C757D] mb-2">Custom permissions</div>
              <div className="space-y-1.5">
                {catalog.permissions.map((p) => (
                  <label key={p.key} className="flex items-start gap-2 p-2 rounded-sm hover:bg-[#F8F9FA] cursor-pointer" data-testid={`perm-${p.key}`}>
                    <input
                      type="checkbox"
                      checked={selected.has(p.key)}
                      onChange={() => toggle(p.key)}
                      className="mt-0.5"
                    />
                    <div className="flex-1">
                      <div className="text-xs font-bold text-[#0B132B]">{p.key}</div>
                      <div className="text-[11px] text-[#6C757D]">{p.description}</div>
                    </div>
                  </label>
                ))}
              </div>

              {user.role === "admin" && (
                <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-sm text-xs text-amber-800" data-testid="admin-note">
                  <b>Note:</b> super-admins have full access to every endpoint regardless of permissions. Toggling these is only useful if you also change this user's role.
                </div>
              )}
            </>
          )}
        </div>

        <div className="px-6 py-3 border-t border-[#E9ECEF] flex items-center justify-end gap-2">
          <Button variant="outline" size="sm" onClick={onClose} className="rounded-sm text-[11px] uppercase tracking-[0.14em] font-bold" data-testid="permissions-cancel">
            Cancel
          </Button>
          <Button size="sm" onClick={save} disabled={busy} className="rounded-sm bg-[#0B132B] hover:bg-[#1C2541] text-white text-[11px] uppercase tracking-[0.14em] font-bold" data-testid="permissions-save">
            Save permissions
          </Button>
        </div>
      </div>
    </div>
  );
}
